import { useCallback, useMemo, useState } from 'react';
import { useUnmount } from 'react-use';

export type AnyStepModel = Record<string, unknown>;
export type StepModel<T extends AnyStepModel> = {
  [K in keyof T]: {
    step: K;
    context: T[K];
  };
}[keyof T];
export type PickStep<T extends AnyStepModel, N extends keyof T> = Extract<T, { step: N }>;

interface UseStepsParams<T extends AnyStepModel> {
  initialValue: StepModel<T>;
}

/**
 * @example
 *
 * ```tsx
 * type ModelOrder = {
 *   initial: undefined;
 *   second: { value: number };
 * };
 * const Steps = createStepComponent<ModelOrder>();
 * const Form = () => {
 *   const { current } = useSteps<ModelOrder>({
 *     initialValue: { step: 'initial', context: undefined }
 *   });
 *
 *   return (
 *     <Steps model={current}>
 *       <Steps.Step step='initial'>{() => <Intro />}</Steps.Step>
 *       <Steps.Step step='second'>{({ value }) => value.toFixed(2)}</Steps.Step>
 *     </Steps>
 *   );
 * };
 * ```
 */
export function useSteps<T extends AnyStepModel>({ initialValue }: UseStepsParams<T>) {
  const [steps, setSteps] = useState<Array<StepModel<T>>>([initialValue]);

  const push = useCallback(
    (value: StepModel<T>) => {
      setSteps((prev) => [...prev, value]);
    },
    [setSteps],
  );

  const back = useCallback(() => {
    setSteps((prev) => {
      if (prev.length === 1) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const resetStep = useCallback(() => {
    setSteps([initialValue]);
  }, [initialValue]);

  useUnmount(() => {
    resetStep();
  });

  return useMemo(
    () => ({
      current: steps.at(-1)!,
      push,
      back,
      resetStep,
    }),
    [push, back, resetStep, steps],
  );
}
